import { START_LOCATION } from 'vue-router'
import type { NavigationFailure, NavigationGuardWithThis, RouteLocationNormalized, RouterScrollBehavior } from 'vue-router'

// How long a restored position is held while the page is still loading its content.
const RESTORE_WAIT = 2000

// The fixed header's height, published by LandingNavbar: an anchor lands below it.
const HEADER_HEIGHT_PROPERTY = '--sw-header-height'

/**
 * The page scrolls inside <body> (html and body clip overflow-x, the body is
 * as tall as the viewport), not the window vue-router scrolls by itself. This
 * keeps the body's position per history entry: a new page starts at the top,
 * or at the element its hash names, back and forward return to where the page
 * was left.
 */
export function createBodyScroll() {
  const positions = new Map<string, number>()
  let entry: string | null = null
  let restore = 0

  // The attempt that has turned the body's scroll anchoring off.
  let holding = 0

  // vue-router's own key of a history entry (see its getScrollKey).
  function entryKey(fullPath: string) {
    return `${window.history.state?.position ?? ''}${fullPath}`
  }

  // The body position that shows the element a hash names (vue-router has
  // decoded it) just below the fixed header; null until the page renders it.
  function anchorTop(hash: string): number | null {
    const el = document.getElementById(hash.slice(1))
    if (!el)
      return null

    const header = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue(HEADER_HEIGHT_PROPERTY)) || 0

    return document.body.scrollTop + el.getBoundingClientRect().top - header
  }

  // Moves the body towards `top` and returns where it is. Only when that moves
  // it: setting it in place would stop a smooth scroll the user has just
  // started (e.g. Contact us).
  function moveBody(top: number) {
    const body = document.body
    const reachable = Math.max(0, Math.min(top, body.scrollHeight - body.clientHeight))

    if (Math.abs(reachable - body.scrollTop) >= 1)
      body.scrollTop = top

    return body.scrollTop
  }

  // Scroll anchoring back on, unless a newer attempt holds a position by now.
  function release(attempt: number) {
    if (holding !== attempt)
      return

    holding = 0
    document.body.style.overflowAnchor = ''
  }

  // A page still loading its content may not be tall enough for the position
  // yet, or not show the anchor yet. And as content arrives above the position,
  // the browser would move the body along with what is on screen (scroll
  // anchoring: back on the orders, the rows load and the footer stays in view).
  // So the body is held there for a moment, with scroll anchoring off, unless
  // the user scrolls or navigates on. The top of a new page needs no holding.
  function scrollBody(target: number | (() => number | null)) {
    const body = document.body
    const attempt = ++restore
    const until = Date.now() + RESTORE_WAIT
    let applied: number | null = null

    if (target === 0) {
      body.scrollTop = 0

      return
    }

    holding = attempt
    body.style.overflowAnchor = 'none'

    // A page that got shorter than the position pulls the body up with it:
    // the browser moved it, not the user.
    const pulledUp = () => applied !== null && body.scrollTop < applied && body.scrollTop >= body.scrollHeight - body.clientHeight - 1

    const step = () => {
      const scrolled = applied !== null && body.scrollTop !== applied && !pulledUp()

      if (attempt !== restore || scrolled || Date.now() >= until) {
        release(attempt)

        return
      }

      const top = typeof target === 'number' ? target : target()
      if (top !== null)
        applied = moveBody(top)

      requestAnimationFrame(step)
    }

    step()
  }

  // Before every navigation, while the old page is still shown.
  const remember: NavigationGuardWithThis<undefined> = () => {
    restore++
    if (entry !== null)
      positions.set(entry, document.body.scrollTop)
  }

  // After the history entry has changed.
  function track(to: RouteLocationNormalized, _from: RouteLocationNormalized, failure?: NavigationFailure | void) {
    if (!failure)
      entry = entryKey(to.fullPath)
  }

  const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
    // A page load leaves the body where the browser put it, unless the address
    // names an element (e.g. /#contact linked from elsewhere): the browser
    // looks for it before the app has rendered it.
    if (from === START_LOCATION) {
      if (to.hash)
        scrollBody(() => anchorTop(to.hash))

      return savedPosition || { top: 0 }
    }

    // Back and forward.
    if (savedPosition) {
      scrollBody(positions.get(entryKey(to.fullPath)) ?? 0)

      return savedPosition
    }

    // Another query or hash of the same page (e.g. a tab of the account page).
    if (to.path === from.path)
      return false

    scrollBody(to.hash ? () => anchorTop(to.hash) : 0)

    return { top: 0 }
  }

  return { remember, track, scrollBehavior }
}
